export type Item = {
  id: string;
  kind: string;
  name: string;
  image: string;
  color: string;
  weapon?: string;
  min: number;
  max: number;
  allowedTeams: number[];
};

export type Saved = {
  id: string;
  item: Item;
  stickers: string[];
  charm: string;
  teams: number[];
  decorations: Record<string, Item>;
  /** Сохранённая категория износа (ключ из WEAR_KEYS) или пусто у старых записей. */
  condition?: string;
};

export const PAGE_SIZE = 60;

export const TEAM_T = 2;
export const TEAM_CT = 3;

/** Модели для быстрого выбора. Список получен из каталога backend (items.json). */
export const MODELS: Record<string, string[]> = {
  weapon: [
    "AK-47", "M4A4", "M4A1-S", "AWP", "Desert Eagle", "Glock-18", "USP-S",
    "P250", "Five-SeveN", "Tec-9", "CZ75-Auto", "Dual Berettas", "P2000",
    "R8 Revolver", "FAMAS", "Galil AR", "AUG", "SG 553", "SSG 08", "SCAR-20",
    "G3SG1", "MAC-10", "MP9", "MP7", "MP5-SD", "UMP-45", "P90", "PP-Bizon",
    "Nova", "XM1014", "MAG-7", "Sawed-Off", "M249", "Negev", "Zeus x27",
  ],
  knife: [
    "★ Karambit", "★ M9 Bayonet", "★ Bayonet", "★ Butterfly Knife",
    "★ Flip Knife", "★ Gut Knife", "★ Falchion Knife", "★ Huntsman Knife",
    "★ Bowie Knife", "★ Shadow Daggers", "★ Talon Knife", "★ Stiletto Knife",
    "★ Ursus Knife", "★ Navaja Knife", "★ Nomad Knife", "★ Skeleton Knife",
    "★ Survival Knife", "★ Paracord Knife", "★ Classic Knife", "★ Kukri Knife",
  ],
  glove: [
    "★ Driver Gloves", "★ Specialist Gloves", "★ Sport Gloves",
    "★ Hand Wraps", "★ Moto Gloves", "★ Bloodhound Gloves",
    "★ Broken Fang Gloves", "★ Hydra Gloves",
  ],
};

export const WEAR_KEYS = [
  "factory_new",
  "minimal_wear",
  "field_tested",
  "well_worn",
  "battle_scarred",
] as const;

/** Границы float для категорий износа: те же, что проверяет backend. */
const WEAR_RANGES: Record<(typeof WEAR_KEYS)[number], [number, number]> = {
  factory_new: [0, 0.07],
  minimal_wear: [0.07, 0.15],
  field_tested: [0.15, 0.38],
  well_worn: [0.38, 0.45],
  battle_scarred: [0.45, 1.000001],
};

/** Категории износа, в которых этот скин существует (по диапазону float предмета). */
export function availableWears(item: Pick<Item, "min" | "max" | "name">): string[] {
  if (!splitName(item.name).skin) return [];
  return WEAR_KEYS.filter((k) => {
    const [from, to] = WEAR_RANGES[k];
    const low = Math.max(from, item.min ?? 0);
    const high = Math.min(to, item.max ?? 1);
    return low <= high && low < to;
  });
}

const RARITY_BY_COLOR: Record<string, string> = {
  "#b0c3d9": "consumer",
  "#5e98d9": "industrial",
  "#4b69ff": "milspec",
  "#8847ff": "restricted",
  "#d32ce6": "classified",
  "#eb4b4b": "covert",
  "#e4ae39": "contraband",
};

/** Ключ i18n редкости по цвету каталога; для ножей/перчаток — особая редкость. */
export function rarityKey(item: Pick<Item, "color" | "kind">): string {
  if (item.kind === "knife" || item.kind === "glove") return "skins.rarity.special";
  const id = RARITY_BY_COLOR[item.color?.toLowerCase()];
  return id ? `skins.rarity.${id}` : "";
}

export function splitName(name: string): { model: string; skin: string } {
  const i = name.indexOf(" | ");
  if (i < 0) return { model: name, skin: "" };
  return { model: name.slice(0, i), skin: name.slice(i + 3) };
}

export function decorationName(item?: Item): string {
  return item ? item.name.replace(/^(Sticker|Charm)\s*\|\s*/i, "") : "";
}

/** Поисковая строка для выбранной модели (каталог ищет подстроку в названии). */
export function modelQuery(model: string): string {
  return model ? `${model} |` : "";
}

/** Слот экипировки: у оружия — модель, у ножей и перчаток — тип. */
export const slotOf = (i: Item): string => (i.kind === "weapon" ? i.weapon || i.id : i.kind);
